import { mockTemplateCategoryIds } from '@/features/templates';
import type { CatalogKind, CatalogOption } from '../models/templateForm.types';

/**
 * The line items' masters behind `env.ENABLE_MOCKS`: two locations, two categories, and products each of one
 * category and linked to one or more locations -- so the Product dropdown visibly follows both.
 */
interface MockProduct extends CatalogOption {
  categoryId: number;
  locationIds: readonly number[];
}

export const MOCK_LOCATIONS: readonly CatalogOption[] = [
  { id: 501, code: 'LOC-001', name: 'Pune Plant', label: 'LOC-001 - Pune Plant' },
  { id: 502, code: 'LOC-002', name: 'Mumbai Warehouse', label: 'LOC-002 - Mumbai Warehouse' },
];

export const MOCK_CATEGORIES: readonly CatalogOption[] = [
  { id: 601, code: 'CAT-001', name: 'IT Hardware', label: 'CAT-001 - IT Hardware' },
  { id: 602, code: 'CAT-002', name: 'Office Supplies', label: 'CAT-002 - Office Supplies' },
];

const MOCK_PRODUCTS: readonly MockProduct[] = [
  { id: 701, code: 'PRD-001', name: 'Laptop', label: 'PRD-001 - Laptop', uomName: 'Each', purchaseGroup: 'P01', costCenterCode: '0000002201', categoryId: 601, locationIds: [501, 502] },
  { id: 702, code: 'PRD-002', name: 'Monitor', label: 'PRD-002 - Monitor', uomName: 'Each', categoryId: 601, locationIds: [501] },
  { id: 703, code: 'PRD-003', name: 'Printer Paper', label: 'PRD-003 - Printer Paper', uomName: 'Ream', categoryId: 602, locationIds: [502] },
];

const strip = ({ id, code, name, label, uomName, purchaseGroup, costCenterCode }: CatalogOption): CatalogOption => ({
  id,
  code,
  name,
  label,
  uomName,
  ...(purchaseGroup !== undefined ? { purchaseGroup } : {}),
  ...(costCenterCode !== undefined ? { costCenterCode } : {}),
});

export async function mockCatalogOptions(
  kind: CatalogKind,
  query: { locationId?: number | null; categoryId?: number | null; templateId?: number | null },
): Promise<CatalogOption[]> {
  if (kind === 'locations') return Promise.resolve([...MOCK_LOCATIONS]);
  if (kind === 'categories') {
    /* A request's lines take only its PR Template's categories (2026-10-09). */
    const own = query.templateId != null ? mockTemplateCategoryIds(query.templateId) : null;
    return Promise.resolve(MOCK_CATEGORIES.filter((category) => !own || own.includes(category.id)));
  }
  return Promise.resolve(
    MOCK_PRODUCTS.filter(
      (product) =>
        product.categoryId === query.categoryId &&
        query.locationId != null &&
        product.locationIds.includes(query.locationId),
    ).map(strip),
  );
}

/** A line's labels, as the server snapshots them -- or null where the product is not offered for the line. */
export function mockLineLabels(line: { locationId: number; categoryId: number; productId: number }) {
  const location = MOCK_LOCATIONS.find((option) => option.id === line.locationId);
  const category = MOCK_CATEGORIES.find((option) => option.id === line.categoryId);
  const product = MOCK_PRODUCTS.find(
    (option) =>
      option.id === line.productId && option.categoryId === line.categoryId && option.locationIds.includes(line.locationId),
  );
  return location && category && product
    ? { locationLabel: location.label, categoryLabel: category.label, productLabel: product.label }
    : null;
}